// import axios from 'axios';
import types from '../../mutation-types';
import MessageTemplateAPI, {
  transformPhoenixTemplateToLegacy,
} from '../../../api/messageTemplates';

export const actions = {
  getMessageTemplates: async (
    { commit, rootGetters },
    { accountId, requestParams }
  ) => {
    try {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isFetching: true });
      commit(types.CLEAR_MESSAGE_TEMPLATES);

      // Check if account has phoenix flag
      const isPhoenix = rootGetters['accounts/isPhoenix'];

      let data;
      if (isPhoenix) {
        // Use phoenix route for phoenix accounts
        const response = await MessageTemplateAPI.getPhoenixTemplates(
          accountId,
          requestParams
        );
        data = response.data;
      } else {
        // Use legacy route for non-phoenix accounts
        const response = await MessageTemplateAPI.getTemplates(
          accountId,
          requestParams
        );
        data = response.data;
      }

      commit(types.SET_MESSAGE_TEMPLATES, data);
      // commit(types.SET_MESSAGE_TEMPLATES_META, data.meta);
    } catch (error) {
      // TODO some action
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isFetching: false });
    }
  },

  getAllMessageTemplates: async (
    { commit, rootGetters },
    { accountId, requestParams = {} }
  ) => {
    let data = null;
    try {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isFetching: true });
      const params = { ...requestParams };
      const shouldAppend = Boolean(params.append);
      delete params.append;

      if (!shouldAppend) {
        commit(types.CLEAR_MESSAGE_TEMPLATES);
      }

      // Check if account has phoenix flag
      const isPhoenix = rootGetters['accounts/isPhoenix'];

      if (isPhoenix) {
        // Use phoenix route for phoenix accounts (approved templates)
        const response = await MessageTemplateAPI.getAllPhoenixTemplates(
          accountId,
          params
        );
        data = response.data;
      } else {
        // Use legacy route for non-phoenix accounts
        const response = await MessageTemplateAPI.getAllTemplates(
          accountId,
          params
        );
        data = response.data;
      }

      if (shouldAppend) {
        commit(types.APPEND_MESSAGE_TEMPLATES, data);
      } else {
        commit(types.SET_MESSAGE_TEMPLATES, data);
      }
      // commit(types.SET_MESSAGE_TEMPLATES_META, data.meta);
    } catch (error) {
      // TODO some action
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isFetching: false });
    }

    return data;
  },

  getAllMessageTemplatesName: async ({ commit }, { accountId }) => {
    try {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isFetching: true });
      commit(types.CLEAR_MESSAGE_TEMPLATES);

      const { data } = await MessageTemplateAPI.getAllTemplatesName(accountId);
      commit(types.SET_MESSAGE_TEMPLATES_NAME, data);
      // commit(types.SET_MESSAGE_TEMPLATES_META, data.meta);
    } catch (error) {
      // TODO some action
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isFetching: false });
    }
  },

  getMessageTemplatesByName: async (
    { commit, rootGetters, state },
    { names, accountId }
  ) => {
    try {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isFetching: true });

      // Check if this is a Phoenix account
      const isPhoenix = rootGetters['accounts/isPhoenix'];

      const inputNames = Array.isArray(names)
        ? names
        : String(names || '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);

      if (isPhoenix && accountId !== undefined && accountId !== null) {
        // Phoenix: Check main cache first, collect templates that need fetching
        const templatesCache = state.records?.templates || [];
        const templatesToFetch = [];

        for (const name of inputNames) {
          // Strip timestamp prefix for Phoenix templates (e.g., "1760396314_template" -> "template")
          const lookupName = String(name).replace(/^\d+_/, '').toLowerCase();

          // Check if template already exists in main cache (check both name and externalName)
          const existingTemplate = templatesCache.find((t) => {
            if (!t) return false;
            const templateName = (t.name || '').toLowerCase();
            const externalName = (t.externalName || '').toLowerCase();
            return templateName === lookupName || externalName === lookupName;
          });

          if (!existingTemplate) {
            // Template not in cache, mark for fetching
            templatesToFetch.push(name);
          }
        }

        // Fetch all missing templates in parallel
        if (templatesToFetch.length > 0) {
          const fetchTemplateByExternalName = async (name) => {
            try {
              const response =
                await MessageTemplateAPI.getPhoenixTemplatesByExternalName(
                  accountId,
                  name
                );
              return response?.data?.data || [];
            } catch (error) {
              return [];
            }
          };

          // Wait for all fetches to complete
          const allPhoenixData = await Promise.all(
            templatesToFetch.map((name) => fetchTemplateByExternalName(name))
          );

          // Transform and store all fetched templates
          const allTransformedTemplates = allPhoenixData
            .flat()
            .filter((template) => template && typeof template === 'object')
            .map((template) =>
              transformPhoenixTemplateToLegacy(template, accountId)
            );

          if (allTransformedTemplates.length > 0) {
            // Store all templates in main cache using APPEND (with deduplication)
            commit(types.APPEND_MESSAGE_TEMPLATES, {
              templates: allTransformedTemplates,
              totalCount: state.records.totalCount,
              totalPages: state.records.totalPages,
            });
          }
        }
      } else {
        // Non-Phoenix accounts: fetch from legacy endpoint in one go
        const response = await MessageTemplateAPI.getTemplatesByName(
          inputNames.join(',')
        );
        const data = response.data;

        // Store in main cache
        commit(types.APPEND_MESSAGE_TEMPLATES, {
          templates: Array.isArray(data) ? data : [],
          totalCount: state.records.totalCount,
          totalPages: state.records.totalPages,
        });
      }
    } catch (error) {
      // TODO some action
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isFetching: false });
    }
  },
  getTemplatesCount: async ({ commit }, { accountId }) => {
    try {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isFetching: true });
      commit(types.CLEAR_MESSAGE_TEMPLATE_COUNT);

      const { data } = await MessageTemplateAPI.getTemplatesCount(accountId);
      commit(types.SET_MESSAGE_TEMPLATE_COUNT, data);
      // commit(types.SET_MESSAGE_TEMPLATES_META, data.meta);
    } catch (error) {
      // TODO some action
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isFetching: false });
    }
  },

  getMessageTemplate: async (
    { commit, rootGetters, state },
    { templateId }
  ) => {
    try {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isTemplateFetching: true });
      const isPhoenix = rootGetters['accounts/isPhoenix'];

      if (isPhoenix) {
        // For Phoenix accounts, prefer the already-fetched list item to avoid legacy endpoint
        const existing = state.records?.templates?.find(
          // eslint-disable-next-line no-underscore-dangle
          (t) => t && t._id === templateId
        );
        if (existing) {
          commit(types.CLEAR_MESSAGE_TEMPLATE);
          commit(types.SET_MESSAGE_TEMPLATE, existing);
          return;
        }
        // Fallback: attempt legacy endpoint only if necessary (may fail on Phoenix)
      }

      const { data } = await MessageTemplateAPI.getTemplate(templateId);
      commit(types.CLEAR_MESSAGE_TEMPLATE);
      commit(types.SET_MESSAGE_TEMPLATE, data);
    } catch (error) {
      // TODO some action
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, {
        isTemplateFetching: false,
      });
    }
  },

  // eslint-disable-next-line no-empty-pattern
  createMessageTemplate: async ({ commit }, { accountId, newTemplate }) => {
    try {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isCreating: true });

      const response = await MessageTemplateAPI.createTemplate(
        accountId,
        newTemplate
      );

      if (!response.data) {
        throw new Error();
      }

      return response.data;
    } catch (error) {
      throw new Error();
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isCreating: false });
    }
  },

  addMessageTemplateTranslation: async (
    { commit },
    { templateId, translation }
  ) => {
    try {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isUpdating: true });

      const response = await MessageTemplateAPI.addTemplateTranslation(
        templateId,
        translation
      );

      if (!response.data) {
        throw new Error();
      }

      return response.data;
    } catch (error) {
      return null;
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isUpdating: false });
    }
  },

  // eslint-disable-next-line no-empty-pattern
  updateMessageTemplate: async ({ commit }, { templateId, template }) => {
    try {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isUpdating: true });
      await MessageTemplateAPI.updateTemplate(templateId, template);
    } catch (error) {
      throw new Error();
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isUpdating: false });
    }
  },

  // eslint-disable-next-line no-empty-pattern
  deleteMessageTemplate: async (
    { commit, rootGetters, state },
    { templateId, accountId }
  ) => {
    let isSuccess = false;

    try {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isDeleting: true });
      commit(types.SET_DELETING_MESSAGE_TEMPLATE, templateId);

      // Check if account has phoenix flag
      const isPhoenix = rootGetters['accounts/isPhoenix'];

      if (isPhoenix) {
        // Phoenix route: delete by name
        // Find template in list first, fallback to state.record
        const template =
          state.records?.templates?.find(
            // eslint-disable-next-line no-underscore-dangle
            (t) => t && t._id === templateId
          ) || state.record;

        const templateName = template?.name || template?.title;

        if (!templateName) {
          throw new Error('Missing template name for Phoenix delete');
        }

        // Check if all translations are drafts (only draft status, not rejected)
        // Note: explicitly check length > 0 to avoid empty array returning true from every()
        const translations = template?.translations || [];
        const allDrafts =
          translations.length > 0 &&
          translations.every((t) => {
            const normalizedStatus = (t.status || '').toLowerCase();
            return normalizedStatus === 'draft';
          });

        if (allDrafts) {
          // All drafts: use draft-only deletion route with accountId
          if (!accountId) {
            throw new Error(
              'Account ID is required for Phoenix draft template delete'
            );
          }

          await MessageTemplateAPI.deletePhoenixDraftTemplateByName(
            templateName,
            accountId
          );
        } else {
          // Approved or rejected: use main templates endpoint with wabaId or phoneNumber
          let wabaId = template?.wabaId;
          let phoneNumber = template?.phoneNumber;

          // Fallback: if neither on template, try WhatsApp inbox
          if (!wabaId && !phoneNumber) {
            const whatsAppInboxes =
              rootGetters['inboxes/getWhatsAppInboxes'] || [];
            const whatsAppInbox = whatsAppInboxes[0];
            if (whatsAppInbox) {
              phoneNumber =
                whatsAppInbox.phone_number ||
                whatsAppInbox?.additional_attributes?.number ||
                whatsAppInbox?.additional_attributes?.phone_number;
            }
          }

          if (!wabaId && !phoneNumber) {
            throw new Error(
              'Missing wabaId or phoneNumber for Phoenix template delete'
            );
          }

          await MessageTemplateAPI.deletePhoenixTemplateByName(templateName, {
            wabaId,
            phoneNumber,
          });
        }
      } else {
        // Use legacy route
        await MessageTemplateAPI.deleteTemplate(templateId);
      }

      isSuccess = true;
    } catch (error) {
      console.error('[deleteMessageTemplate] Failed to delete template:', {
        templateId,
        accountId,
        isPhoenix: rootGetters['accounts/isPhoenix'],
        error: error?.message || error,
      });
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isDeleting: false });
      commit(types.CLEAR_DELETING_MESSAGE_TEMPLATE, templateId);
    }

    return isSuccess;
  },

  deleteMessageTemplateTranslation: async (
    { commit, rootGetters },
    { templateId, language, translationId, accountId, status, wabaId, phoneNumber }
  ) => {
    let isSuccess = false;

    try {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isDeleting: true });
      const isPhoenix = rootGetters['accounts/isPhoenix'];

      if (isPhoenix) {
        // Phoenix: can only delete draft or rejected translations individually
        const normalizedStatus = (status || '').toLowerCase();
        const isDraft = normalizedStatus === 'draft';
        const isRejected = normalizedStatus === 'rejected';

        if (!isDraft && !isRejected) {
          throw new Error(
            'Only draft or rejected translations can be deleted individually'
          );
        }

        if (!translationId) {
          throw new Error(
            'Translation ID is required for Phoenix WhatsApp delete'
          );
        }

        if (isDraft) {
          // Draft: use drafts endpoint with accountId
          if (!accountId) {
            throw new Error(
              'Account ID is required for Phoenix draft template delete'
            );
          }
          await MessageTemplateAPI.deletePhoenixDraftTemplateTranslation(
            translationId,
            accountId
          );
        } else {
          // Rejected: use main templates endpoint with wabaId/phoneNumber
          let resolvedWabaId = wabaId;
          let resolvedPhoneNumber = phoneNumber;

          // Fallback: if neither provided, try WhatsApp inbox
          if (!resolvedWabaId && !resolvedPhoneNumber) {
            const whatsAppInboxes =
              rootGetters['inboxes/getWhatsAppInboxes'] || [];
            const whatsAppInbox = whatsAppInboxes[0];
            if (whatsAppInbox) {
              resolvedPhoneNumber =
                whatsAppInbox.phone_number ||
                whatsAppInbox?.additional_attributes?.number ||
                whatsAppInbox?.additional_attributes?.phone_number;
            }
          }

          if (!resolvedWabaId && !resolvedPhoneNumber) {
            throw new Error(
              'Missing wabaId or phoneNumber for Phoenix rejected translation delete'
            );
          }

          await MessageTemplateAPI.deletePhoenixRejectedTemplateTranslation(
            translationId,
            { wabaId: resolvedWabaId, phoneNumber: resolvedPhoneNumber }
          );
        }
      } else {
        await MessageTemplateAPI.deleteTemplateTranslation(templateId, language);
      }

      isSuccess = true;
    } catch (error) {
      console.error(
        '[deleteMessageTemplateTranslation] Failed to delete translation:',
        {
          templateId,
          translationId,
          language,
          status,
          isPhoenix: rootGetters['accounts/isPhoenix'],
          error: error?.message || error,
        }
      );
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isDeleting: false });
    }

    return isSuccess;
  },

  getMessageTemplatesEntrypoints: async (
    { commit, rootGetters, state },
    { accountId, templateId, templateName }
  ) => {
    let isSuccess = false;

    try {
      commit(types.CLEAR_MESSAGE_TEMPLATE_ENTRYPOINTS);
      const isPhoenix = rootGetters['accounts/isPhoenix'];

      if (isPhoenix) {
        // Phoenix: fetch template by name to ensure details are available; entrypoints concept not applicable here
        const nameToQuery =
          templateName || state.record?.name || state.record?.title || '';
        await MessageTemplateAPI.getPhoenixTemplateByName(
          accountId,
          nameToQuery,
          { grouped: true }
        );
        // Do not set entrypoints from this response; keep it empty
        commit(types.SET_MESSAGE_TEMPLATE_ENTRYPOINTS, []);
        isSuccess = true;
      } else {
        const { data } = await MessageTemplateAPI.getTemplateEntrypoints(
          accountId,
          templateId
        );

        if (typeof data?.length !== 'number') {
          throw new Error();
        }

        commit(types.SET_MESSAGE_TEMPLATE_ENTRYPOINTS, data);
        isSuccess = true;
      }
    } catch (error) {
      // TODO some action
    } finally {
      // TODO some action
    }

    return isSuccess;
  },

  getScheduledMessageTemplates: async (
    { commit },
    { accountId, requestParams }
  ) => {
    try {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, {
        isScheduledFetching: true,
      });

      const { data } = await MessageTemplateAPI.getScheduledTemplates(
        accountId,
        requestParams
      );

      commit(types.CLEAR_SCHEDULED_MESSAGE_TEMPLATES);
      commit(types.SET_SCHEDULED_MESSAGE_TEMPLATES, data);
      // commit(types.SET_MESSAGE_TEMPLATES_META, data.meta);
    } catch (error) {
      // TODO some action
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, {
        isScheduledFetching: false,
      });
    }
  },

  getScheduledMessageTemplate: async ({ commit }, { scheduledId }) => {
    try {
      const { data } =
        await MessageTemplateAPI.getScheduledTemplate(scheduledId);

      commit(types.CLEAR_SCHEDULED_MESSAGE_TEMPLATE);
      commit(types.SET_SCHEDULED_MESSAGE_TEMPLATE, data);
      // commit(types.SET_MESSAGE_TEMPLATES_META, data.meta);
    } catch (error) {
      // TODO some action
    }
  },

  cancelScheduledMessageTemplate: async ({ commit }, { id }) => {
    try {
      const { data } =
        await MessageTemplateAPI.cancelScheduledMessageTemplate(id);
      commit(types.CLEAR_MESSAGE_TEMPLATE);
      commit(types.SET_MESSAGE_TEMPLATE, data);
    } catch (error) {
      // TODO some action
    }
  },

  // SMS message templates
  getSmsMessageTemplates: async (
    { commit, rootGetters },
    { accountId, requestParams }
  ) => {
    try {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isFetching: true });
      commit(types.CLEAR_MESSAGE_TEMPLATES);

      const isPhoenix = rootGetters['accounts/isPhoenix'];
      let data;

      if (isPhoenix) {
        const response = await MessageTemplateAPI.getPhoenixSmsTemplates(
          accountId,
          requestParams
        );
        data = response.data;
      } else {
        const response = await MessageTemplateAPI.getSmsTemplates(
          accountId,
          requestParams
        );
        data = response.data;
      }

      commit(types.SET_MESSAGE_TEMPLATES, data);
      // commit(types.SET_MESSAGE_TEMPLATES_META, data.meta);
    } catch (error) {
      // TODO some action
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isFetching: false });
    }
  },

  getSmsTemplatesCount: async ({ commit }, { accountId }) => {
    try {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isFetching: true });
      commit(types.CLEAR_MESSAGE_TEMPLATE_COUNT);

      const { data } = await MessageTemplateAPI.getSmsTemplatesCount(accountId);
      commit(types.SET_MESSAGE_TEMPLATE_COUNT, data);
      // commit(types.SET_MESSAGE_TEMPLATES_META, data.meta);
    } catch (error) {
      // TODO some action
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isFetching: false });
    }
  },

  getSmsMessageTemplate: async (
    { commit, rootGetters, state },
    { templateId }
  ) => {
    try {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isTemplateFetching: true });
      const isPhoenix = rootGetters['accounts/isPhoenix'];

      if (isPhoenix) {
        // For Phoenix accounts, find template in loaded list only
        // Do not make API call to legacy endpoint
        const existing = state.records?.smsTemplates?.find(
          // eslint-disable-next-line no-underscore-dangle
          (t) => t && t._id === templateId
        );

        if (existing) {
          commit(types.CLEAR_MESSAGE_TEMPLATE);
          commit(types.SET_MESSAGE_TEMPLATE, existing);
          return;
        }

        // If template not found in loaded list, clear and return
        // Don't call legacy API for Phoenix accounts
        commit(types.CLEAR_MESSAGE_TEMPLATE);
        return;
      }

      // Legacy fetch for non-Phoenix accounts only
      const { data } = await MessageTemplateAPI.getSmsTemplate(templateId);
      commit(types.CLEAR_MESSAGE_TEMPLATE);
      commit(types.SET_MESSAGE_TEMPLATE, data);
    } catch (error) {
      // TODO some action
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, {
        isTemplateFetching: false,
      });
    }
  },

  createSmsMessageTemplate: async ({ commit }, { accountId, newTemplate }) => {
    try {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isCreating: true });

      const response = await MessageTemplateAPI.createSmsTemplate(
        accountId,
        newTemplate
      );

      if (!response.data) {
        throw new Error();
      }

      return response.data;
    } catch (error) {
      throw new Error();
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isCreating: false });
    }
  },

  addSmsMessageTemplateTranslation: async (
    { commit },
    { templateId, translation }
  ) => {
    try {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isUpdating: true });

      const response = await MessageTemplateAPI.addSmsTemplateTranslation(
        templateId,
        translation
      );

      if (!response.data) {
        throw new Error();
      }

      return response.data;
    } catch (error) {
      return null;
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isUpdating: false });
    }
  },

  // eslint-disable-next-line no-empty-pattern
  updateSmsMessageTemplate: async ({ commit }, { templateId, template }) => {
    try {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isUpdating: true });
      await MessageTemplateAPI.updateSmsTemplate(templateId, template);
    } catch (error) {
      throw new Error();
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isUpdating: false });
    }
  },

  deleteSmsMessageTemplate: async (
    { commit, rootGetters, state },
    { templateId, accountId }
  ) => {
    let isSuccess = false;

    try {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isDeleting: true });
      commit(types.SET_DELETING_MESSAGE_TEMPLATE, templateId);

      const isPhoenix = rootGetters['accounts/isPhoenix'];

      if (isPhoenix) {
        const template =
          state.records?.smsTemplates?.find(
            // eslint-disable-next-line no-underscore-dangle
            (t) => t && t._id === templateId
          ) || state.record;

        const templateName = template?.name || template?.title;

        if (!templateName) {
          throw new Error('Missing template name for Phoenix SMS delete');
        }

        // For SMS Phoenix, we delete by name
        await MessageTemplateAPI.deletePhoenixSmsTemplateByName(
          accountId,
          templateName
        );
      } else {
        await MessageTemplateAPI.deleteSmsTemplate(templateId);
      }

      isSuccess = true;
    } catch (error) {
      // TODO some action
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isDeleting: false });
      commit(types.CLEAR_DELETING_MESSAGE_TEMPLATE, templateId);
    }

    return isSuccess;
  },

  deleteSmsMessageTemplateTranslation: async (
    { commit, rootGetters },
    { templateId, language, translationId, accountId }
  ) => {
    let isSuccess = false;

    try {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isDeleting: true });
      const isPhoenix = rootGetters['accounts/isPhoenix'];

      if (isPhoenix) {
        if (!translationId) {
          throw new Error('Translation ID is required for Phoenix SMS delete');
        }

        await MessageTemplateAPI.deletePhoenixSmsTemplateTranslation(
          accountId,
          translationId
        );
      } else {
        // Use legacy route for non-Phoenix accounts
        await MessageTemplateAPI.deleteSmsTemplateTranslation(
          templateId,
          language
        );
      }

      isSuccess = true;
    } catch (error) {
      // TODO some action
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, { isDeleting: false });
    }

    return isSuccess;
  },

  setUiFlag: ({ commit }, newFlags) => {
    try {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAGS, newFlags);
    } catch (error) {
      // TODO some action
    }
  },
};
